import { useState } from "react";
import { Save, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ICON_OPTIONS } from "@/lib/icons";

const POSITIONS = [
  { value: "top-left", label: "Top · Left" },
  { value: "top-right", label: "Top · Right" },
  { value: "bottom-left", label: "Bottom · Left" },
  { value: "bottom-right", label: "Bottom · Right" },
];

const VARIANTS = [
  { value: "soft", label: "Soft (peach icon)" },
  { value: "orange", label: "Orange (filled icon)" },
];

export const BannerEditor = ({ banners = [], onChange, testIdPrefix = "banner", helperText }) => {
  const list = banners || [];

  const setAt = (i, patch) => {
    const next = list.map((b, idx) => (idx === i ? { ...b, ...patch } : b));
    onChange(next);
  };
  const add = () => onChange([
    ...list,
    { icon: "Sparkles", label: "NEW BANNER", position: "top-right", variant: "soft", visible: true },
  ]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div data-testid={`${testIdPrefix}-editor`} className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-base">Floating banners</Label>
          {helperText && (
            <p className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">{helperText}</p>
          )}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`${testIdPrefix}-add`} className="rounded-lg">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add banner
        </Button>
      </div>

      {list.length === 0 && (
        <p data-testid={`${testIdPrefix}-empty`} className="text-xs text-[hsl(var(--muted-foreground))] rounded-xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--accent))] px-3 py-3">
          No banners yet. Use “Add banner” to place a floating tile next to the phone.
        </p>
      )}

      <div className="space-y-3">
        {list.map((b, i) => (
          <div
            key={i}
            data-testid={`${testIdPrefix}-row-${i}`}
            className="rounded-xl border border-[hsl(var(--border))] bg-white p-3 sm:p-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
              <div className="sm:col-span-3 space-y-1">
                <Label className="text-xs">Icon</Label>
                <Select value={b.icon || "Sparkles"} onValueChange={(v) => setAt(i, { icon: v })}>
                  <SelectTrigger data-testid={`${testIdPrefix}-row-${i}-icon`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-5 space-y-1">
                <Label className="text-xs">Label</Label>
                <Input
                  data-testid={`${testIdPrefix}-row-${i}-label`}
                  value={b.label || ""}
                  onChange={(e) => setAt(i, { label: e.target.value })}
                  placeholder="e.g. WAG-O-METER · 92%"
                />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <Label className="text-xs">Position</Label>
                <Select value={b.position || "top-left"} onValueChange={(v) => setAt(i, { position: v })}>
                  <SelectTrigger data-testid={`${testIdPrefix}-row-${i}-position`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POSITIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2 space-y-1">
                <Label className="text-xs">Style</Label>
                <Select value={b.variant || "soft"} onValueChange={(v) => setAt(i, { variant: v })}>
                  <SelectTrigger data-testid={`${testIdPrefix}-row-${i}-variant`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VARIANTS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-2 flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(i)}
                data-testid={`${testIdPrefix}-row-${i}-remove`}
                className="text-[hsl(var(--muted-foreground))] hover:text-[#D94A14] rounded-lg"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BannerEditor;
